import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';

interface AdminContextValue {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  isAttorney: boolean;
  isLoading: boolean;
  checkAdminAccess: () => Promise<void>;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [isAttorney, setIsAttorney] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAdminAccess = async () => {
    try {
      // Wait for auth to finish loading
      if (auth.isLoading) {
        return;
      }

      if (!auth.user || !auth.isAuthenticated) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsOrgAdmin(false);
        setIsAttorney(false);
        setIsLoading(false);
        return;
      }

      // Get user's groups from ID token profile
      const profile = auth.user.profile;
      
      // Cognito groups are in the 'cognito:groups' claim
      // It might be a string (JSON array) or already parsed array
      let groups: string[] = [];
      const cognitoGroups = profile?.['cognito:groups'];
      
      if (typeof cognitoGroups === 'string') {
        // Parse if it's a JSON string like "[SuperAdmins]"
        try {
          groups = JSON.parse(cognitoGroups);
        } catch {
          // If not JSON, might be single group
          groups = [cognitoGroups];
        }
      } else if (Array.isArray(cognitoGroups)) {
        groups = cognitoGroups;
      }
      
      const isSA = groups.includes('SuperAdmins');
      const isOA = groups.includes('OrgAdmins');
      const isAtt = groups.includes('Attorneys');
      const isAd = isSA || isOA;

      setIsSuperAdmin(isSA);
      setIsOrgAdmin(isOA);
      setIsAttorney(isAtt);
      setIsAdmin(isAd);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsOrgAdmin(false);
      setIsAttorney(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAdminAccess();
  }, [auth.isAuthenticated, auth.user, auth.isLoading]);

  return (
    <AdminContext.Provider value={{ isAdmin, isSuperAdmin, isOrgAdmin, isAttorney, isLoading, checkAdminAccess }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

